import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Loader2, MapPin, SlidersHorizontal, Check } from 'lucide-react';
import { format, addDays, isSameDay, parseISO, getDay } from 'date-fns';
import { getCalendarBookings, CalendarBooking } from '../../lib/bookings';
import { getCalendarFieldQuotes } from '../../lib/fieldQuotes/calendarFieldQuotes';
import { getResources, Resource } from '../../lib/resources';
import {
  ResourceWorkingAvailability,
  getBusinessResourcesWorkingAvailability,
} from '../../lib/resourceAvailability';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import { getCalendarTasks } from '../../lib/tasks';
import { getAssets } from '../../lib/assets';
import { getBusinessAssetsAvailability } from '../../lib/assetAvailability';

type WeekViewFilter = 'all' | 'resources' | 'assets';

interface GoogleCalendarEvent {
  id: string;
  title: string | null;
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
  resource_id: string | null;
  description: string | null;
}

interface GhostBooking {
  date: string;
  time: string;
  durationMinutes: number;
  resourceId?: string;
}

interface MiniWeekViewProps {
  businessId: string;
  weekStartDate: Date;
  onWeekChange: (newStart: Date) => void;
  onDayClick?: (date: Date) => void;
  onTimeSlotClick?: (date: Date, time: string, resourceId: string, isAsset?: boolean) => void;
  selectedDate?: string;
  ghostBooking?: GhostBooking | null;
}

const HOUR_HEIGHT = 60;
const RESOURCE_COL_WIDTH = 80;

function getContrastText(hex: string): string {
  if (!hex || hex.length < 7) return '#1f2937';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1f2937' : '#ffffff';
}

function computeHoursFromBusinessHours(
  weekDays: Date[],
  businessHours?: Record<string, any> | null
): { startHour: number; endHour: number } {
  if (!businessHours) return { startHour: 6, endHour: 23 };

  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  let min = 24;
  let max = 0;

  weekDays.forEach(day => {
    const dh = businessHours[dayNames[day.getDay()]];
    if (dh && dh.enabled && !dh.closed) {
      const [oh, om] = dh.open.split(':').map(Number);
      const [ch, cm] = dh.close.split(':').map(Number);
      min = Math.min(min, oh + om / 60);
      max = Math.max(max, ch + cm / 60);
    }
  });

  if (min >= 24 || max <= 0) return { startHour: 6, endHour: 23 };
  return { startHour: Math.floor(min), endHour: Math.ceil(max) };
}

function isSlotAvailable(
  halfStart: number,
  halfEnd: number,
  day: Date,
  avail: ResourceWorkingAvailability | null
): boolean {
  if (!avail) return false;

  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[getDay(day)] as keyof ResourceWorkingAvailability;
  const da = avail[dayName] as any;

  if (!da || !da.enabled || !da.shifts || da.shifts.length === 0) return false;

  for (const shift of da.shifts) {
    const [sh, sm] = shift.start.split(':').map(Number);
    const [eh, em] = shift.end.split(':').map(Number);
    const sDecimal = sh + sm / 60;
    const eDecimal = eh + em / 60;
    if (halfStart >= sDecimal && halfEnd <= eDecimal) return true;
  }
  return false;
}

function getBookingsForResourceAndDay(
  bookings: CalendarBooking[],
  resourceId: string,
  day: Date
): CalendarBooking[] {
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  return bookings.filter(booking => {
    try {
      const bStart = parseISO(booking.start);
      const bEnd = parseISO(booking.end);
      if (!(bStart < dayEnd && bEnd > dayStart)) return false;

      if (resourceId === 'unassigned') {
        const hasRes = booking.extendedProps?.resources && booking.extendedProps.resources.length > 0;
        const hasAssets = booking.extendedProps?.booking_assets && booking.extendedProps.booking_assets.length > 0;
        return !hasRes && !hasAssets;
      }

      const resIds = (booking.extendedProps?.resources || []).map((r: any) => r?.id).filter(Boolean);
      const assetIds = (booking.extendedProps?.booking_assets || []).map((a: any) => a?.id || a?.asset_id).filter(Boolean);

      return (
        resIds.includes(resourceId) ||
        assetIds.includes(resourceId) ||
        booking.resourceId === resourceId ||
        booking.extendedProps?.resource_id === resourceId
      );
    } catch {
      return false;
    }
  });
}

export default function MiniWeekView({
  businessId,
  weekStartDate,
  onWeekChange,
  onDayClick,
  onTimeSlotClick,
  selectedDate,
  ghostBooking,
}: MiniWeekViewProps) {
  const { selectedBusiness } = useBusiness();
  const [allBookings, setAllBookings] = useState<CalendarBooking[]>([]);
  const [gcalEvents, setGcalEvents] = useState<GoogleCalendarEvent[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [assetColumns, setAssetColumns] = useState<Resource[]>([]);
  const [availabilities, setAvailabilities] = useState<Map<string, ResourceWorkingAvailability>>(new Map());
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Assets in the grid + the same view filter as CalendarV2's week view
  // (shared business setting, so the default follows the calendar and back).
  const [assetsEnabled, setAssetsEnabled] = useState(false);
  const [weekViewFilter, setWeekViewFilter] = useState<WeekViewFilter>('all');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadSettings() {
      if (!businessId || !supabase) return;
      try {
        const [{ data: enabled }, { data: filterData }] = await Promise.all([
          supabase.rpc('get_app_setting', {
            p_business_id: businessId,
            p_app_slug: 'teams',
            p_setting_key: 'assets_enabled',
            p_default_value: false,
          }),
          supabase.rpc('get_app_setting', {
            p_business_id: businessId,
            p_app_slug: 'calendar',
            p_setting_key: 'week_view_filter',
            p_default_value: 'all',
          }),
        ]);
        if (cancelled) return;
        setAssetsEnabled(enabled === true);
        if (filterData === 'all' || filterData === 'resources' || filterData === 'assets') {
          setWeekViewFilter(filterData);
        }
      } catch {
        if (!cancelled) setAssetsEnabled(false);
      }
    }
    loadSettings();
    return () => { cancelled = true; };
  }, [businessId]);

  useEffect(() => {
    if (!filterMenuOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target as Node)) setFilterMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [filterMenuOpen]);

  const changeWeekViewFilter = async (value: WeekViewFilter) => {
    setWeekViewFilter(value);
    setFilterMenuOpen(false);
    if (!supabase || !businessId) return;
    try {
      // Same setting CalendarV2 uses — saving here updates the calendar's
      // default too (and vice versa). The event keeps open views in sync.
      await supabase.rpc('set_app_setting', {
        p_business_id: businessId,
        p_app_slug: 'calendar',
        p_setting_key: 'week_view_filter',
        p_setting_value: value,
      });
      window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
        detail: { key: 'week_view_filter', value },
      }));
    } catch (err) {
      console.error('Failed to save week view filter:', err);
    }
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStartDate, i));
  const { startHour, endHour } = computeHoursFromBusinessHours(weekDays, selectedBusiness?.business_hours);
  const totalHours = endHour - startHour;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!businessId || !supabase) return;
      setLoading(true);
      try {
        const start = weekDays[0];
        const end = addDays(weekDays[6], 1);
        const fmtStart = format(start, 'yyyy-MM-dd');
        const fmtEnd = format(end, 'yyyy-MM-dd');

        const [
          mobileBookings,
          locationBookings,
          resourceData,
          availData,
          timeOffResult,
          fieldQuoteEvents,
          bufferResult,
          gcalResult,
          taskEvents,
        ] = await Promise.all([
          getCalendarBookings(businessId, start, end, false),
          getCalendarBookings(businessId, start, end, true),
          getResources(businessId),
          getBusinessResourcesWorkingAvailability(businessId),
          // Time-off requests
          supabase
            .from('time_off_requests')
            .select('id, start_date, end_date, request_type, is_partial_day, start_time, end_time, resource_id, resources:resource_id (id, first_name, last_name)')
            .eq('business_id', businessId)
            .eq('status', 'approved')
            .lte('start_date', fmtEnd)
            .gte('end_date', fmtStart),
          getCalendarFieldQuotes(businessId, start, end),
          // Buffer blocks
          supabase
            .from('mobile_bookings')
            .select('id, resource_id, asset_id, booking_id, start_date, start_time, end_date, end_time, buffer_time, is_buffer_block')
            .eq('business_id', businessId)
            .eq('is_buffer_block', true)
            .lte('start_date', fmtEnd)
            .gte('end_date', fmtStart),
          // Google Calendar events
          supabase
            .from('google_calendar_events')
            .select('id, title, start_time, end_time, all_day, resource_id, description')
            .eq('business_id', businessId)
            .eq('is_deleted', false)
            .gte('start_time', start.toISOString())
            .lte('start_time', end.toISOString()),
          // Tasks
          getCalendarTasks(businessId, start, end),
        ]);

        if (cancelled) return;

        // Resources
        const active = resourceData
          .filter(r => r.status === 'active' && !r.not_bookable)
          .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
        setResources(active);

        const map = new Map<string, ResourceWorkingAvailability>();
        availData.forEach(a => map.set(a.resource_id, a));

        // Assets as bookable columns (same mapping CalendarV2 uses)
        if (assetsEnabled) {
          try {
            const [assetData, assetAvail] = await Promise.all([
              getAssets(businessId),
              getBusinessAssetsAvailability(businessId),
            ]);
            if (!cancelled) {
              const activeAssets = assetData
                .filter(a => a.status === 'active' && !a.not_bookable)
                .sort((a, b) => a.name.localeCompare(b.name));
              setAssetColumns(activeAssets.map(asset => ({
                id: asset.id,
                business_id: asset.business_id,
                first_name: asset.name,
                last_name: '',
                status: asset.status,
                avatar_url: asset.image_url || undefined,
                _isAsset: true,
                _assetType: asset.asset_type,
              } as any)));
              assetAvail.forEach((avail: any) => {
                map.set(avail.asset_id, { ...avail, resource_id: avail.asset_id } as any);
              });
            }
          } catch (assetErr) {
            console.error('MiniWeekView: failed to load assets', assetErr);
            if (!cancelled) setAssetColumns([]);
          }
        } else {
          setAssetColumns([]);
        }

        setAvailabilities(map);

        // Transform time-off to CalendarBooking[]
        const timeOffEvents: CalendarBooking[] = (timeOffResult.data || []).map((req: any) => {
          const res = Array.isArray(req.resources) ? req.resources[0] : req.resources;
          const resName = res ? `${res.first_name} ${res.last_name}` : 'Unknown';
          const typeLabels: Record<string, string> = { vacation: 'Vacation', sick: 'Sick Leave', personal: 'Personal Day', family: 'Family Emergency', bereavement: 'Bereavement', other: 'Other' };
          const label = typeLabels[req.request_type] || req.request_type;
          let endDateStr = req.end_date;
          if (!req.is_partial_day) {
            const [y, m, d] = req.end_date.split('-').map(Number);
            endDateStr = format(addDays(new Date(y, m - 1, d), 1), 'yyyy-MM-dd');
          }
          return {
            id: `timeoff_${req.id}`,
            title: `${label} - ${resName}`,
            booking_number: 0,
            start: req.is_partial_day && req.start_time ? `${req.start_date}T${req.start_time}:00` : req.start_date,
            end: req.is_partial_day && req.end_time ? `${req.end_date}T${req.end_time}:00` : endDateStr,
            backgroundColor: '#000000',
            borderColor: '#000000',
            allDay: !req.is_partial_day,
            display: 'block',
            resourceId: req.resource_id,
            extendedProps: {
              customer: resName, status: 'Time Off', booking_number: 0, subtotal: 0, total_amount: 0,
              is_time_off: true, resource_name: resName,
              resources: res ? [{ id: res.id, first_name: res.first_name, last_name: res.last_name }] : [],
              resource_id: req.resource_id,
            },
          } as CalendarBooking;
        });

        // Transform field quotes to CalendarBooking[]
        // (already mapped by getCalendarFieldQuotes)

        // Transform buffer blocks to CalendarBooking[]
        const bufferEvents: CalendarBooking[] = (bufferResult.data || []).map((block: any) => {
          const [sy, sm, sd] = block.start_date.split('-').map(Number);
          const [sh, smi] = block.start_time.split(':').map(Number);
          const sObj = new Date(sy, sm - 1, sd, sh, smi, 0, 0);
          const [ey, em, ed] = block.end_date.split('-').map(Number);
          const [eh, emi] = block.end_time.split(':').map(Number);
          const eObj = new Date(ey, em - 1, ed, eh, emi, 0, 0);
          const ownerId = block.resource_id || block.asset_id;
          return {
            id: `buffer-${block.id}`,
            title: 'Buffer',
            booking_number: 0,
            start: sObj.toISOString(),
            end: eObj.toISOString(),
            backgroundColor: '#000000',
            borderColor: '#000000',
            secondaryColor: null,
            allDay: false,
            display: 'block',
            resourceId: ownerId,
            extendedProps: {
              customer: '', booking_number: 0, subtotal: 0, total_amount: 0, status: 'buffer',
              is_buffer_block: true, buffer_time: block.buffer_time, resource_id: ownerId,
              resources: block.resource_id ? [{ id: block.resource_id, first_name: '', last_name: '' }] : [],
              booking_assets: block.asset_id ? [{ id: block.asset_id, asset_id: block.asset_id }] : [],
            },
          } as CalendarBooking;
        });

        setAllBookings([...mobileBookings, ...locationBookings, ...bufferEvents, ...timeOffEvents, ...fieldQuoteEvents, ...taskEvents]);
        setGcalEvents(gcalResult.data || []);
      } catch (err) {
        console.error('MiniWeekView: failed to load data', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [businessId, weekStartDate, assetsEnabled]);

  const getBlockStyle = (booking: CalendarBooking) => {
    try {
      const bStart = parseISO(booking.start);
      const bEnd = parseISO(booking.end);
      const startMin = bStart.getHours() * 60 + bStart.getMinutes();
      const endMin = bEnd.getHours() * 60 + bEnd.getMinutes();
      const duration = Math.max(endMin - startMin, 30);
      const topMin = startMin - startHour * 60;
      const top = Math.max(0, (topMin / 60) * HOUR_HEIGHT);
      const height = Math.max((duration / 60) * HOUR_HEIGHT, 16);
      return { top, height, color: booking.backgroundColor || '#3b82f6' };
    } catch {
      return { top: 0, height: 16, color: '#3b82f6' };
    }
  };

  const today = new Date();
  // Columns = resources + assets, trimmed by the shared week-view filter.
  const columns = [
    ...(weekViewFilter === 'assets' ? [] : resources),
    ...(weekViewFilter === 'resources' ? [] : assetColumns),
  ];
  const resourceCount = Math.max(columns.length, 1);
  const dayWidth = resourceCount * RESOURCE_COL_WIDTH;
  const totalGridWidth = weekDays.length * dayWidth;

  const FILTER_OPTIONS: Array<{ value: WeekViewFilter; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'resources', label: 'Team members' },
    { value: 'assets', label: 'Assets' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Week navigation */}
      <div className="flex items-center justify-between px-2 py-2 border-b border-gray-200 flex-shrink-0">
        <button
          type="button"
          onClick={() => onWeekChange(addDays(weekStartDate, -7))}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 text-gray-600" />
        </button>
        <span className="text-xs font-semibold text-gray-700">
          {format(weekDays[0], 'MMM d')} — {format(weekDays[6], 'MMM d, yyyy')}
        </span>
        <span className="flex items-center gap-0.5">
          {/* View filter — same setting as CalendarV2's week view */}
          {assetsEnabled && (
            <div className="relative" ref={filterMenuRef}>
              <button
                type="button"
                onClick={() => setFilterMenuOpen(v => !v)}
                className={`p-1.5 rounded-lg transition-colors ${weekViewFilter !== 'all' ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-100 text-gray-600'}`}
                title="Filter columns (team members / assets)"
                aria-label="Filter week view columns"
              >
                <SlidersHorizontal className="h-4 w-4" />
              </button>
              {filterMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-40 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden py-1 z-30">
                  {FILTER_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => changeWeekViewFilter(opt.value)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-[13px] font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {opt.label}
                      {weekViewFilter === opt.value && <Check className="h-3.5 w-3.5 text-blue-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => onWeekChange(addDays(weekStartDate, 7))}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-gray-600" />
          </button>
        </span>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      )}

      {!loading && (
        <div ref={scrollRef} className="overflow-x-auto overflow-y-auto flex-1">
          <div style={{ width: totalGridWidth, minWidth: totalGridWidth }}>
            {/* Sticky header: day names + resource avatars */}
            <div className="flex sticky top-0 z-10 bg-white border-b border-gray-200">
              {weekDays.map((day, dayIdx) => {
                const isToday = isSameDay(day, today);
                const isSelected = selectedDate && isSameDay(day, new Date(selectedDate + 'T00:00:00'));

                return (
                  <div
                    key={dayIdx}
                    className={`border-r border-gray-200 last:border-r-0 cursor-pointer ${isSelected ? 'bg-blue-50/60' : ''}`}
                    style={{ width: dayWidth }}
                    onClick={() => onDayClick?.(day)}
                  >
                    <div className={`flex items-center justify-center gap-1 py-1.5 ${isToday ? 'bg-blue-50' : ''}`}>
                      <span className={`text-xs font-semibold ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>{format(day, 'EEE')}</span>
                      <span className={`text-sm font-bold ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>{format(day, 'MMM d')}</span>
                    </div>
                    <div className="h-8 border-t border-gray-200 bg-white flex">
                      {columns.map((resource) => {
                        const isAsset = !!(resource as any)._isAsset;
                        const initials = isAsset
                          ? (() => {
                              const name = (resource.first_name || '').trim();
                              const parts = name.split(/\s+/);
                              return parts.length >= 2
                                ? `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase()
                                : (name[0] || 'A').toUpperCase();
                            })()
                          : `${resource.first_name?.[0] || ''}${resource.last_name?.[0] || ''}`.toUpperCase();
                        const isMobile = !isAsset && Boolean(resource.is_mobile_available);

                        return (
                          <div
                            key={`hdr-${dayIdx}-${resource.id}`}
                            className="flex-1 border-r border-gray-100 last:border-r-0 flex items-center justify-center gap-1"
                            style={{ minWidth: RESOURCE_COL_WIDTH, maxWidth: RESOURCE_COL_WIDTH, width: RESOURCE_COL_WIDTH }}
                            title={`${resource.first_name} ${resource.last_name}`}
                          >
                            {isAsset ? (
                              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white text-[10px] font-semibold flex-shrink-0">
                                {initials}
                              </div>
                            ) : (
                              <>
                                {resource.avatar_url ? (
                                  <>
                                    <img
                                      src={resource.avatar_url}
                                      alt={`${resource.first_name} ${resource.last_name}`}
                                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const parent = target.parentElement;
                                        if (parent) {
                                          const fb = parent.querySelector('.mini-initials-badge') as HTMLElement;
                                          if (fb) fb.style.display = 'flex';
                                        }
                                      }}
                                    />
                                    <div className="mini-initials-badge hidden items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] font-semibold flex-shrink-0">
                                      {initials}
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] font-semibold flex-shrink-0">
                                    {initials}
                                  </div>
                                )}
                                {isMobile && <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
                              </>
                            )}
                          </div>
                        );
                      })}
                      {columns.length === 0 && (
                        <div className="flex-1 flex items-center justify-center text-[10px] text-gray-400">
                          {weekViewFilter === 'assets' ? 'No assets' : 'No resources'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Time grid body */}
            <div className="flex relative">
              {weekDays.map((day, dayIdx) => {
                const isSelected = selectedDate && isSameDay(day, new Date(selectedDate + 'T00:00:00'));

                return (
                  <div
                    key={dayIdx}
                    className={`relative ${isSelected ? 'bg-blue-50/30' : dayIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                    style={{ width: dayWidth, height: totalHours * HOUR_HEIGHT }}
                    onClick={() => onDayClick?.(day)}
                  >
                    {dayIdx < weekDays.length - 1 && (
                      <div className="absolute top-0 bottom-0" style={{ right: '-1px', width: '2px', backgroundColor: '#9CA3AF', zIndex: 5 }} />
                    )}

                    {columns.map((resource, resIdx) => {
                      const resourceBookings = getBookingsForResourceAndDay(allBookings, resource.id, day);
                      const colLeft = resIdx * RESOURCE_COL_WIDTH;
                      const resAvail = availabilities.get(resource.id) || null;

                      return (
                        <div
                          key={`col-${dayIdx}-${resource.id}`}
                          className="absolute top-0"
                          style={{
                            left: colLeft,
                            width: RESOURCE_COL_WIDTH,
                            height: totalHours * HOUR_HEIGHT,
                            borderRight: resIdx < columns.length - 1 ? '1px solid #E5E7EB' : 'none',
                          }}
                        >
                          {/* Time slots with labels + 30min dividers + availability greying */}
                          {Array.from({ length: totalHours }, (_, i) => {
                            const hour = startHour + i;
                            const period = hour >= 12 ? 'p' : 'a';
                            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            const halfHeight = HOUR_HEIGHT / 2;

                            const firstHalfAvail = isSlotAvailable(hour, hour + 0.5, day, resAvail);
                            const secondHalfAvail = isSlotAvailable(hour + 0.5, hour + 1, day, resAvail);

                            return (
                              <div
                                key={i}
                                className="absolute left-0 right-0"
                                style={{ top: i * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                              >
                                {/* Top-of-hour cell */}
                                <div
                                  className={`absolute left-0 right-0 top-0 border-b border-gray-200 transition-colors cursor-pointer ${
                                    firstHalfAvail ? 'hover:bg-blue-50/40' : ''
                                  }`}
                                  style={{ height: halfHeight, backgroundColor: firstHalfAvail ? undefined : 'rgba(209,213,219,0.45)' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const timeStr = `${String(hour).padStart(2, '0')}:00`;
                                    onTimeSlotClick?.(day, timeStr, resource.id, !!(resource as any)._isAsset);
                                  }}
                                >
                                  <span className="absolute top-0 left-0.5 text-[7px] text-gray-400 leading-none select-none pointer-events-none">
                                    {displayHour}:00{period}
                                  </span>
                                </div>
                                {/* Half-hour cell */}
                                <div
                                  className={`absolute left-0 right-0 border-b border-gray-100 transition-colors cursor-pointer ${
                                    secondHalfAvail ? 'hover:bg-blue-50/40' : ''
                                  }`}
                                  style={{ top: halfHeight, height: halfHeight, backgroundColor: secondHalfAvail ? undefined : 'rgba(209,213,219,0.45)' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const timeStr = `${String(hour).padStart(2, '0')}:30`;
                                    onTimeSlotClick?.(day, timeStr, resource.id, !!(resource as any)._isAsset);
                                  }}
                                >
                                  <span className="absolute top-0 left-0.5 text-[7px] text-gray-400 leading-none select-none pointer-events-none">
                                    {displayHour}:30{period}
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          {/* Booking / buffer / time-off / field-quote blocks */}
                          {resourceBookings.map(booking => {
                            const { top, height, color } = getBlockStyle(booking);
                            const textColor = getContrastText(color);
                            const isBuffer = booking.extendedProps?.is_buffer_block;
                            const isTimeOff = booking.extendedProps?.is_time_off;
                            const isFieldQuote = booking.extendedProps?.is_field_quote;
                            const customerName = booking.extendedProps?.customer || '';
                            const bStartTime = booking.extendedProps?.start_time;
                            const displayTime = bStartTime
                              ? format(parseISO(`2000-01-01T${bStartTime}`), 'h:mma')
                              : '';

                            return (
                              <div
                                key={`${booking.id}-${resource.id}`}
                                className="absolute rounded-[3px] overflow-hidden px-0.5 py-0.5"
                                style={{
                                  top,
                                  height,
                                  left: 2,
                                  right: 2,
                                  backgroundColor: color,
                                  color: textColor,
                                  fontSize: '8px',
                                  lineHeight: '1.2',
                                  zIndex: isBuffer ? 1 : 2,
                                  opacity: isBuffer ? 0.5 : 1,
                                  ...(isBuffer ? { backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(255,255,255,0.15) 3px, rgba(255,255,255,0.15) 6px)' } : {}),
                                }}
                                title={
                                  isBuffer ? `Buffer (${resource.first_name} ${resource.last_name})`
                                  : isTimeOff ? booking.title || 'Time Off'
                                  : isFieldQuote ? `${booking.title} — ${customerName}`
                                  : `${customerName} — ${displayTime} (${resource.first_name} ${resource.last_name})`
                                }
                              >
                                {height >= 18 && (
                                  <div className="font-semibold truncate">
                                    {isBuffer ? 'Buffer' : isTimeOff ? (booking.extendedProps as any)?.time_off_type || 'Time Off' : customerName}
                                  </div>
                                )}
                                {height >= 30 && !isBuffer && displayTime && (
                                  <div className="truncate opacity-80">{displayTime}</div>
                                )}
                              </div>
                            );
                          })}

                          {/* Google Calendar events */}
                          {gcalEvents
                            .filter(ge => {
                              if (!ge.start_time) return false;
                              const geStart = parseISO(ge.start_time);
                              const isThisDay = isSameDay(geStart, day);
                              const isForResource = !ge.resource_id || ge.resource_id === resource.id;
                              return isThisDay && isForResource;
                            })
                            .map(ge => {
                              if (!ge.start_time || !ge.end_time) return null;
                              const geStart = parseISO(ge.start_time);
                              const geEnd = parseISO(ge.end_time);
                              const startDecimal = geStart.getHours() + geStart.getMinutes() / 60;
                              const endDecimal = geEnd.getHours() + geEnd.getMinutes() / 60;
                              // All-day events fill the visible day instead of a sliver at midnight.
                              const top = ge.all_day ? 0 : (startDecimal - startHour) * HOUR_HEIGHT;
                              const height = ge.all_day ? totalHours * HOUR_HEIGHT : Math.max((endDecimal - startDecimal) * HOUR_HEIGHT, 16);
                              if (top + height < 0 || top > totalHours * HOUR_HEIGHT) return null;
                              return (
                                <div
                                  key={`gcal-${ge.id}-${resource.id}`}
                                  className="absolute rounded-[3px] overflow-hidden border-l-2 border-blue-400"
                                  style={{
                                    top: Math.max(top, 0),
                                    height,
                                    left: 2,
                                    right: 2,
                                    backgroundColor: 'rgba(66, 133, 244, 0.15)',
                                    zIndex: 3,
                                    padding: '1px 2px',
                                  }}
                                  title={ge.title || '(No title)'}
                                >
                                  {height >= 16 && (
                                    <div className="text-[7px] font-medium text-blue-700 truncate">{ge.title || '(No title)'}</div>
                                  )}
                                  {height >= 28 && (
                                    <div className="text-[6px] text-blue-500 truncate">
                                      {ge.all_day ? 'All day' : format(geStart, 'h:mm a')}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                          {/* Ghost booking skeleton — multi-day, availability-aware, only on selected resource column */}
                          {ghostBooking && ghostBooking.date && ghostBooking.time && (!ghostBooking.resourceId || ghostBooking.resourceId === resource.id) && (() => {
                            const [gh, gm] = ghostBooking.time.split(':').map(Number);
                            const ghostStartDt = new Date(`${ghostBooking.date}T${ghostBooking.time}`);
                            const ghostEndDt = new Date(ghostStartDt.getTime() + Math.max(ghostBooking.durationMinutes, 30) * 60000);
                            const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
                            const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60000);

                            if (ghostEndDt <= dayStart || ghostStartDt >= dayEnd) return null;

                            const isFirstDay = isSameDay(ghostStartDt, day);
                            const isLastDay = isSameDay(ghostEndDt, day) || (ghostEndDt > dayStart && ghostEndDt <= dayEnd);

                            const sliceStart = isFirstDay ? gh + gm / 60 : startHour;
                            const sliceEnd = isLastDay
                              ? ghostEndDt.getHours() + ghostEndDt.getMinutes() / 60
                              : endHour;

                            const step = 0.5;
                            const segments: { from: number; to: number }[] = [];
                            let segStart: number | null = null;

                            for (let t = sliceStart; t < sliceEnd; t += step) {
                              const avail = isSlotAvailable(t, t + step, day, resAvail);
                              if (avail) {
                                if (segStart === null) segStart = t;
                              } else {
                                if (segStart !== null) {
                                  segments.push({ from: segStart, to: t });
                                  segStart = null;
                                }
                              }
                            }
                            if (segStart !== null) segments.push({ from: segStart, to: sliceEnd });

                            if (segments.length === 0) return null;

                            const fmtTime = (dt: Date) => {
                              const h = dt.getHours();
                              const m = dt.getMinutes();
                              const ap = h >= 12 ? 'pm' : 'am';
                              const dh12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                              return `${dh12}:${String(m).padStart(2, '0')}${ap}`;
                            };
                            const timeLabel = isFirstDay
                              ? `${fmtTime(ghostStartDt)} – ${fmtTime(ghostEndDt)}`
                              : isLastDay
                                ? `ends ${fmtTime(ghostEndDt)}`
                                : 'all day';

                            return segments.map((seg, segIdx) => {
                              const segTop = Math.max((seg.from - startHour) * HOUR_HEIGHT, 0);
                              const segBottom = Math.min((seg.to - startHour) * HOUR_HEIGHT, totalHours * HOUR_HEIGHT);
                              const segHeight = segBottom - segTop;
                              if (segHeight <= 0) return null;

                              const isTopSeg = segIdx === 0;
                              const isBotSeg = segIdx === segments.length - 1;
                              const topRound = isTopSeg && (isFirstDay || segIdx > 0);
                              const botRound = isBotSeg && (isLastDay || segIdx < segments.length - 1);
                              const radius = topRound && botRound ? 4
                                : topRound ? '4px 4px 0 0'
                                : botRound ? '0 0 4px 4px'
                                : 0;

                              return (
                                <div
                                  key={`ghost-seg-${segIdx}`}
                                  className="absolute pointer-events-none"
                                  style={{
                                    top: segTop,
                                    height: segHeight,
                                    left: 1,
                                    right: 1,
                                    backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                    border: '2px dashed rgba(59, 130, 246, 0.5)',
                                    borderRadius: radius,
                                    zIndex: 8,
                                    padding: '2px 3px',
                                  }}
                                >
                                  {isTopSeg && segHeight >= 18 && (
                                    <div className="text-[8px] font-semibold text-blue-600 truncate">New Booking</div>
                                  )}
                                  {isTopSeg && segHeight >= 32 && (
                                    <div className="text-[7px] text-blue-500 truncate">{timeLabel}</div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
